import Button from "@mui/material/Button";
import { HttpStatusCode } from "axios";
import {
  adaptAxiosError,
  CONNECTION_FAILURE_MESSAGE,
  readApiError,
  UNEXPECTED_BAD_REQUEST_CODE,
  VALIDATION_ERROR_CODE,
  type ApiErrorResult,
} from "_shared/_errors/api-error";
import { reportShownError } from "_shared/_errors/client-error-reporter";
import {
  closeSnackbar,
  enqueueSnackbar,
  type SnackbarKey,
  type SnackbarOptions,
} from "_shared/_providers/snackbars/snackbars";

/**
 * Every error snackbar does one of two things (#6614):
 *
 * 1. It tells the person something they can change to fix it: the field, the item or the person, and
 *    what to do.
 * 2. Otherwise it says our support team has been notified, gives a short reference, and reports the
 *    error to Sentry, so that the sentence is true.
 *
 * Raw internal text never reaches the person. A server's `detail` is shown only when the server marks
 * it reader-facing, by answering with a v1 problem whose `code` names the refusal. The generic codes
 * below say the opposite: the prose under them was written for a log, not for a reader.
 */

/** Problem codes whose `detail` is not written for the reader. */
const GENERIC_PROBLEM_CODES: ReadonlySet<string> = new Set([
  UNEXPECTED_BAD_REQUEST_CODE,
  // A controller's own ProblemDetails, rewritten by the API's 400 filter. Its prose is often an
  // exception message: "CollectionItemUpdated requires an existing item and value." (#6612).
  "bad_request",
]);

/** The message the API's 400 filter puts on a field it cannot describe. It names nothing to change. */
const GENERIC_FIELD_MESSAGE = "The value is invalid.";

export type ErrorSnackbarCategory = "actionable" | "refused" | "missing" | "stale" | "support";

export type ErrorSnackbarRecovery = {
  /** The button's text: "Refresh", "Reload the draft". */
  readonly label: string;
  readonly onRecover: () => void;
};

export type ErrorSnackbarContext = {
  /**
   * What the person was doing, as it reads after "Something went wrong": "saving your proposal
   * changes", "uploading your files".
   */
  readonly doing: string;
  /** A stable name for the failed operation, sent to Sentry: "proposal.save". */
  readonly operation: string;
  /**
   * The page's own sentence for this failure, when the page recognises it and can say what to change.
   * It wins over everything the helper would say.
   */
  readonly message?: string;
  /** What a 403 says when the server gave no reader-facing reason. */
  readonly refusedMessage?: string;
  /** What a 404 says when the server gave no reader-facing reason. */
  readonly missingMessage?: string;
  /** What a 409 or 412 says when the server gave no reader-facing reason. */
  readonly staleMessage?: string;
  /**
   * Offered beside a missing or stale message, and beside the page's own `message`: a page that
   * recognises a failure and names what to change knows whether this recovery is the way out. A save
   * refused for a change to a category the proposal no longer holds offers "Refresh and re-apply my
   * changes" (#6612).
   */
  readonly recovery?: ErrorSnackbarRecovery;
};

export type ErrorSnackbarOptions = Pick<SnackbarOptions, "key" | "persist" | "preventDuplicate" | "autoHideDuration">;

export type ErrorSnackbarDescription = {
  readonly category: ErrorSnackbarCategory;
  readonly message: string;
  /** The status, code and trace the error carried, when the API answered. */
  readonly apiError?: ApiErrorResult;
};

export const DEFAULT_REFUSED_MESSAGE =
  "You don't have access to do this. Ask someone who manages it to give you access.";
export const DEFAULT_MISSING_MESSAGE = "This is no longer here. It may have been deleted. Refresh to see what is.";
export const DEFAULT_STALE_MESSAGE = "This changed after you opened it. Refresh to see the latest, then try again.";

/**
 * Shows an error the person can act on, or says support has been notified and reports it. Use it for
 * every error snackbar; ESLint refuses an `enqueueSnackbar` with `variant: "error"` anywhere else.
 */
export function showErrorSnackbar(
  error: unknown,
  context: ErrorSnackbarContext,
  options: ErrorSnackbarOptions = {},
): SnackbarKey {
  const description = describeError(error, context);
  const message = description.category === "support"
    ? supportMessage(context.doing, reportToSupport(error, description.apiError, context.operation))
    : description.message;
  const recovery = description.category === "missing" || description.category === "stale" || context.message
    ? context.recovery
    : undefined;

  return enqueueSnackbar(message, {
    ...options,
    variant: "error",
    ...(recovery ? { action: (key: SnackbarKey) => recoveryButton(key, recovery) } : {}),
  });
}

/**
 * Shows a sentence the page wrote about something the person can change, with no error behind it: a
 * client-side check that failed before anything was sent.
 */
export function showActionableErrorSnackbar(message: string, options: ErrorSnackbarOptions = {}): SnackbarKey {
  return enqueueSnackbar(message, { ...options, variant: "error" });
}

/**
 * Sorts an error into the category its snackbar belongs to, and says what the snackbar would show. A
 * `support` description's message is a placeholder: the reference exists only once the error is
 * reported, which only {@link showErrorSnackbar} does.
 */
export function describeError(error: unknown, context: ErrorSnackbarContext): ErrorSnackbarDescription {
  const apiError = readError(error);
  if (context.message) return { category: "actionable", message: context.message, apiError };
  if (!apiError) return { category: "support", message: supportMessage(context.doing), apiError };
  if (apiError.category === "connection") return { category: "actionable", message: CONNECTION_FAILURE_MESSAGE, apiError };

  const readerDetail = readerFacingDetail(apiError);
  switch (apiError.status) {
    case HttpStatusCode.Forbidden:
      return { category: "refused", message: readerDetail ?? context.refusedMessage ?? DEFAULT_REFUSED_MESSAGE, apiError };
    case HttpStatusCode.NotFound:
      return { category: "missing", message: readerDetail ?? context.missingMessage ?? DEFAULT_MISSING_MESSAGE, apiError };
    case HttpStatusCode.Conflict:
    case HttpStatusCode.PreconditionFailed:
      return { category: "stale", message: readerDetail ?? context.staleMessage ?? DEFAULT_STALE_MESSAGE, apiError };
  }

  const fieldMessage = readerFacingFieldMessage(apiError);
  if (fieldMessage) return { category: "actionable", message: fieldMessage, apiError };
  if (readerDetail) return { category: "actionable", message: readerDetail, apiError };
  return { category: "support", message: supportMessage(context.doing), apiError };
}

/** "Something went wrong saving your changes. Our support team has been notified (ref 3d5cdba4)." */
export function supportMessage(doing: string, reference?: string): string {
  const notified = reference
    ? `Our support team has been notified (ref ${reference}).`
    : "Our support team has been notified.";
  return `Something went wrong ${doing}. ${notified}`;
}

function readError(error: unknown): ApiErrorResult | undefined {
  const axiosError = adaptAxiosError(error);
  if (axiosError) return axiosError;
  if (!isRecord(error)) return undefined;
  if ("attorneyHubError" in error) return readApiError(error);
  // A thrown Error is the browser's own failure, even when something gave it a `status`.
  if (error instanceof Error) return undefined;
  if ("status" in error) return readApiError(error);
  return undefined;
}

/**
 * A server's `detail`, when the server marked it for the reader: a v1 problem, or an exact legacy
 * mapping a module owns, under a code that names the refusal, for a request the server refused rather
 * than failed.
 */
function readerFacingDetail(error: ApiErrorResult): string | undefined {
  if (!error.trusted || !error.code || GENERIC_PROBLEM_CODES.has(error.code)) return undefined;
  if (error.code === VALIDATION_ERROR_CODE) return undefined;
  if (error.status === undefined || error.status >= HttpStatusCode.InternalServerError) return undefined;
  return error.detail ?? error.title;
}

/**
 * The reasons a v1 problem gives for its fields. The API writes these for the reader: it maps each
 * refusal to a public field alias and a safe message. A message that names nothing to change is left
 * out, and a problem with only those is a fault for support.
 */
function readerFacingFieldMessage(error: ApiErrorResult): string | undefined {
  if (!error.trusted) return undefined;
  const messages = [...new Set(Object.values(error.fieldErrors).flat())]
    .filter((message) => message.trim() && message !== GENERIC_FIELD_MESSAGE);
  return messages.length > 0 ? messages.join(" ") : undefined;
}

/** Reports the error and returns the reference the person can quote. */
function reportToSupport(error: unknown, apiError: ApiErrorResult | undefined, operation: string): string {
  return reportShownError(apiError
    ? {
      operation,
      origin: "server",
      status: apiError.status,
      code: apiError.code,
      traceId: apiError.traceId,
    }
    : { operation, origin: "client", errorName: errorName(error) });
}

function errorName(error: unknown): string {
  if (error instanceof Error) return error.name || error.constructor.name;
  return typeof error;
}

function recoveryButton(key: SnackbarKey, recovery: ErrorSnackbarRecovery) {
  return (
    <Button
      color="inherit"
      size="small"
      onClick={() => {
        closeSnackbar(key);
        recovery.onRecover();
      }}
    >
      {recovery.label}
    </Button>
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
